import { Type, Transform } from 'class-transformer';
import {
  IsInt,
  IsNumber,
  IsIn,
  IsArray,
  IsString,
  IsDateString,
  IsUUID,
  IsNotEmpty,
  ValidateNested,
  IsOptional,
  IsBoolean,
} from 'class-validator';
import { injectable } from 'inversify';

import { HttpService } from '@vk-hr-tek/core/http';
import {
  FiltersResponse,
  Filter,
  FilterService,
  FilterType,
} from '@vk-hr-tek/core/filter';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { QueryService } from '@vk-hr-tek/core/query';

export class UpdateEventTypesByCollectionParams {
  @IsUUID()
  @IsNotEmpty()
  collection_id: string;
}

export class GetCompanyEventTypesByCollectionParams {
  @IsUUID()
  @IsNotEmpty()
  collection_id: string;
}

export class GetEventTypesByCollectionParams {
  @IsUUID()
  @IsNotEmpty()
  collection_id: string;
}

export class UpdateEventTypesByCollectionQuery {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCompanyEventTypesByCollectionQuery {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCollectionsQuery {
  @IsUUID()
  @IsOptional()
  company_id?: string;
}

export type PriorityEnum = string;

export type StartSideEnum = string;

export class EventType {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['employee', 'manager', 'group'], { each: true })
  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  start_side: ('employee' | 'manager' | 'group')[];

  @IsIn(['high', 'medium', 'low'])
  @IsString()
  @IsOptional()
  priority?: 'high' | 'medium' | 'low';

  @IsBoolean()
  @IsNotEmpty()
  is_popular: boolean;
}

export class GetCompanyCollectionsEventTypesResponseCollection {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export class GetCompanyCollectionsEventTypesResponse {
  @ValidateNested()
  @Type(() => GetCompanyCollectionsEventTypesResponseCollection)
  @IsNotEmpty()
  collection: GetCompanyCollectionsEventTypesResponseCollection;

  @ValidateNested({ each: true })
  @Type(() => EventType)
  @IsArray()
  @IsNotEmpty()
  event_types: EventType[];
}

export class EventTypesList {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  company_ids: string[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  event_type_ids: string[];

  @IsIn(['employee', 'manager', 'group'], { each: true })
  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  start_side: ('employee' | 'manager' | 'group')[];

  @IsBoolean()
  @IsNotEmpty()
  is_popular: boolean;
}

export class GetCollectionsEventTypesResponseCollection {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export class GetCollectionsEventTypesResponse {
  @ValidateNested()
  @Type(() => GetCollectionsEventTypesResponseCollection)
  @IsNotEmpty()
  collection: GetCollectionsEventTypesResponseCollection;

  @ValidateNested({ each: true })
  @Type(() => EventTypesList)
  @IsArray()
  @IsNotEmpty()
  event_types: EventTypesList[];
}

export type CollectionTypeEnum = string;

export class Collection {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn([
    'vacations',
    'absences',
    'business_trips',
    'papers',
    'review',
    'competencies',
    'personal',
    'payslips',
    'work_arrangement',
    'another',
  ])
  @IsString()
  @IsNotEmpty()
  type:
    | 'vacations'
    | 'absences'
    | 'business_trips'
    | 'papers'
    | 'review'
    | 'competencies'
    | 'personal'
    | 'payslips'
    | 'work_arrangement'
    | 'another';

  @IsInt()
  @IsOptional()
  event_types_count?: number;
}

export class GetCollectionResponse {
  @ValidateNested({ each: true })
  @Type(() => Collection)
  @IsArray()
  @IsNotEmpty()
  collections: Collection[];
}

export class UpdateEventTypes {
  @ValidateNested({ each: true })
  @Type(() => EventType)
  @IsArray()
  @IsNotEmpty()
  event_types: EventType[];
}

@injectable()
export class EventTypeService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private query: QueryService,
  ) {}

  async updateEventTypesByCollection({
    query,
    params,
    body,
  }: {
    query: UpdateEventTypesByCollectionQuery;
    params: UpdateEventTypesByCollectionParams;
    body: UpdateEventTypes;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, UpdateEventTypesByCollectionQuery),
      this.validator.validateOrReject(
        params,
        UpdateEventTypesByCollectionParams,
      ),
      this.validator.validateOrReject(body, UpdateEventTypes),
    ]);

    let requestPath = '/company/event_type/collections/{collection_id}';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof UpdateEventTypesByCollectionParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    requestPath = `${requestPath}${
      query && Object.keys(query).length
        ? `?${this.query.stringify(query)}`
        : ''
    }`;
    const result = await this.http.put(requestPath, body, {});

    return result;
  }

  async getCompanyEventTypesByCollection({
    query,
    params,
  }: {
    query: GetCompanyEventTypesByCollectionQuery;
    params: GetCompanyEventTypesByCollectionParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetCompanyEventTypesByCollectionQuery,
      ),
      this.validator.validateOrReject(
        params,
        GetCompanyEventTypesByCollectionParams,
      ),
    ]);

    let requestPath = '/company/event_type/collections/{collection_id}';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetCompanyEventTypesByCollectionParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetCompanyCollectionsEventTypesResponse,
    );

    return response;
  }

  async getEventTypesByCollection({
    params,
  }: {
    params: GetEventTypesByCollectionParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetEventTypesByCollectionParams),
    ]);

    let requestPath = '/event_type/collections/{collection_id}';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetEventTypesByCollectionParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetCollectionsEventTypesResponse,
    );

    return response;
  }

  async getCollections({
    query = {},
  }: {
    query?: GetCollectionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetCollectionsQuery),
    ]);

    let requestPath = '/event_type/collections';

    const result = await this.http.get(requestPath, query, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetCollectionResponse,
    );

    return response;
  }
}
